import { useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "../../components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import { Skeleton } from "../../components/ui/skeleton";
import { Sparkles, Plus, PackageOpen, Loader2, Inbox, Pencil, Trash2, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { formatISTDayLabel, todayIST } from "../../lib/datetime";
import { joinParts, plural } from "../../lib/vocabulary";
import { labelFor, roleBreakdown } from "../../lib/population";
import StickyHeading from "../../components/common/StickyHeading";

const ALL = "__all__";

/** Why the ceilings could not be loaded, KEEPING THE CAUSE.
 *  `formatApiErrorDetail(undefined)` answers "Something went wrong" for any
 *  failure with no server detail, which swallows the transport error — so the
 *  detail is checked first and the exception's own message is used when there
 *  is none. A compliance surface that can only say "something went wrong"
 *  cannot be acted on. */
function ucpmpFailure(e) {
  const detail = e?.response?.data?.detail;
  if (detail != null) return formatApiErrorDetail(detail);
  return e?.message || "Could not reach the server.";
}

/** ONE renderer for a compliance field that may not have been captured (F334/
 *  F406). A GAP IS SHOWN AS A GAP: never a blank cell, never an em-dash the
 *  reader has to interpret, and — for money — never ₹0, because a ₹0 sample is
 *  a compliance claim that the issuance had no value. */
function ComplianceValue({ label, value, missingText }) {
  if (value === null || value === undefined || value === "") {
    return (
      <span className="text-orange-700">
        {missingText || `${label} not captured`}
      </span>
    );
  }
  return (
    <span>
      <span className="text-callisto-muted">{label} </span>
      <span className="font-semibold text-callisto-navy">{value}</span>
    </span>
  );
}

/** Roles the `value_unset_reason` diagnostic is written FOR.
 *
 *  Everyone except the Sales Officer. It is an internal note, not field-facing
 *  copy — see SampleValue. */
const INTERNAL_REASON_ROLES = [
  "assistant_manager", "admin_manager", "director", "admin",
];

/** The value of one ledger entry, or the reason it has none.
 *  `value_status` is the backend's own answer — this never infers "unpriced"
 *  from a falsy number, because 0.00 is a cost somebody entered.
 *
 *  THE REASON IS INTERNAL, AND THE SALES OFFICER DOES NOT GET IT. `value_
 *  unset_reason` is written by `ucpmp.unit_cost_for` for whoever maintains the
 *  price master: "no price row is in force for 'Calcitrol' as at 2026-08-10 —
 *  D5/D6 pricing is due before 1 Sept" names a decision id, a source
 *  collection's field and an internal deadline. It was on the rep's OWN
 *  `/samples` rows as a hover title. This is the second time the same note
 *  class reached a rep on this page — `ucpmp.value_ceilings` carries the first
 *  in its own comment ("they were written as engineering notes … and a rep
 *  read all of it"), and that one was fixed by rewriting the sentence for the
 *  field. This one cannot be: it is a maintenance instruction, so the fix is
 *  the audience, not the wording.
 *
 *  THE VISIBLE FACT IS UNCHANGED for everybody. "Value not set" is a gap shown
 *  as a gap (F334/F406) and the rep still sees it — what they no longer get is
 *  the engineering note behind it. The role is read here rather than passed in
 *  because `SamplesTable` renders on both the rep's page and the manager's,
 *  and a prop is a thing a third caller can forget. */
function SampleValue({ row }) {
  const { user } = useAuth();
  const showReason = INTERNAL_REASON_ROLES.includes(user?.role);
  if (row.value_status === "computed" && row.value !== null) {
    return (
      <span>
        <span className="text-callisto-muted">Value </span>
        <span className="font-semibold text-callisto-navy">
          ₹{Number(row.value).toFixed(2)}
        </span>
      </span>
    );
  }
  return (
    <span
      className="text-orange-700"
      title={showReason ? row.value_unset_reason || "" : undefined}
      data-testid={`samples-value-unset-${row.id}`}
    >
      Value not set
    </span>
  );
}

/** Rupees, grouped Indian-style. Returns null for an absent figure rather than
 *  "₹0" — the caller decides what to say about a number it does not have. */
function rupees(amount) {
  if (amount === null || amount === undefined || amount === "") return null;
  const n = Number(amount);
  if (!Number.isFinite(n)) return null;
  return `₹${n.toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
}

/** ONE money ceiling, in whichever of its TWO honest states it is in.
 *
 *  THE STATE THAT DID NOT EXIST BEFORE. Until the sales basis landed, both
 *  money ceilings were permanently not-computable and this panel said so with a
 *  hardcoded line. Now the Director can type an annual domestic-sales figure and
 *  the 2% ceiling COMPUTES from it — while promotional spend to date still has
 *  no complete source. Those are two different facts:
 *
 *    · not computable  — no basis; the ceiling itself is unknown.
 *    · ceiling known, USAGE unknown — the limit is a real number and is shown;
 *      what has been spent against it is not, and is named as not computable.
 *
 *  A hardcoded "Not computable yet" over a computed ceiling hides a number the
 *  Director entered herself. Its opposite — showing the ceiling and letting a
 *  reader assume the blank means zero — is the compliance claim this whole
 *  module refuses to make. So both halves are said out loud, and no usage
 *  figure, percentage or verdict is rendered while `used` is null. */
export function ValueCeiling({ testId, label, block }) {
  if (!block) return null;
  const computable = block.computable === true;
  const ceiling = rupees(block.ceiling);
  const basis = rupees(block.basis_rupees);
  return (
    <div data-testid={testId}>
      <p className="text-sm font-semibold text-callisto-navy">{label}</p>
      {computable && ceiling ? (
        <>
          <p className="text-[11px] text-callisto-navy" data-testid={`${testId}-ceiling`}>
            <span className="font-semibold">Ceiling {ceiling}</span>
            {basis &&
              ` — ${block.limit_pct}% of the ${basis} sales basis recorded for ` +
              `${block.financial_year}${block.basis_recorded_by ? ` by ${block.basis_recorded_by}` : ""}`}
          </p>
          {/* The ceiling is known; the spend against it is not. Never a 0, never
              a "within limit". */}
          <p className="text-[11px] font-semibold text-orange-700">
            Spend to date not computable
          </p>
        </>
      ) : (
        <p className="text-[11px] font-semibold text-orange-700">Not computable yet</p>
      )}
      <ul className="text-[11px] text-callisto-muted list-disc ml-4">
        {(block.missing || []).map((m) => <li key={m}>{m}</li>)}
      </ul>
    </div>
  );
}

/** WHERE THIS DOCTOR STANDS, BEFORE THE ISSUANCE (P1).
 *
 *  The panel below the log answers "who is already over" — which is the wrong
 *  moment, because the packs are out. This sits inside the issue dialog and
 *  answers for the pair the form is holding, while the rep can still act on it.
 *
 *  ONE COMPONENT, BOTH DIALOGS. The rep's form and the admin's on-behalf form
 *  render this same component; a fact that appears in two places has one
 *  implementation.
 *
 *  ADVISORY, AND WORDED AS ADVICE (D-027 — a CLAIM cap refuses, a UCPMP ceiling
 *  advises). Nothing here disables the save button, and the breach sentence says
 *  the issuance will be RECORDED and who will be told.
 *
 *  NOT COMPUTABLE IS NOT A GREEN LIGHT. With no doctor or no drug chosen the
 *  backend answers `computable: false` with the missing input named, and that is
 *  what is shown — not "0 of 12", which reads as twelve packs of headroom. */
export function PackCeilingBeforeIssuing({ standing, error }) {
  if (error) {
    return (
      <div className="rounded-md border border-orange-200 bg-orange-50 text-orange-800 text-[11px] px-3 py-2"
           data-testid="pack-ceiling-error">
        Where this doctor stands against the UCPMP pack ceiling could not be
        loaded: {error}
      </div>
    );
  }
  if (!standing) return null;

  if (!standing.computable) {
    return (
      <div className="rounded-md border border-orange-200 bg-orange-50 px-3 py-2"
           data-testid="pack-ceiling-not-computable">
        <p className="text-[11px] font-semibold text-orange-800">
          UCPMP pack ceiling — not computable yet
        </p>
        <ul className="text-[11px] text-orange-800 list-disc ml-4">
          {(standing.missing || []).map((m) => <li key={m}>{m}</li>)}
        </ul>
      </div>
    );
  }

  const p = standing.projected;
  const tone =
    (p?.state || standing.state) === "breach"
      ? "border-red-200 bg-red-50"
      : (p?.state || standing.state) === "warn"
      ? "border-orange-200 bg-orange-50"
      : "border-callisto bg-callisto-app";
  return (
    <div className={`rounded-md border px-3 py-2 ${tone}`} data-testid="pack-ceiling">
      <p className="text-[11px] text-callisto-navy" data-testid="pack-ceiling-standing">
        <span className="font-semibold">{standing.doctor_name}</span> has had{" "}
        <span className="font-semibold">{standing.used} of {standing.ceiling}</span>{" "}
        packs of {standing.product} in {standing.year} — {standing.remaining} left.
      </p>
      {p && (
        <p className="text-[11px] text-callisto-navy mt-1" data-testid="pack-ceiling-projection">
          Issuing {plural(p.quantity, "pack")} now would make it {p.used} of {standing.ceiling}.
          {p.state === "breach" &&
            " That is over the UCPMP limit. The issuance is still recorded, and the Admin Manager and the Director are notified — this is advice, not a block."}
          {p.state === "warn" &&
            " That is at or past 80% of the limit. You may keep issuing; this is a warning, not a block."}
        </p>
      )}
    </div>
  );
}

/** The standing for the pair the form currently holds, refetched as it changes.
 *
 *  Backend-served, always: the wording for a missing input, the ceiling, the
 *  count and the projection all come from `/samples/ucpmp/doctor`, so the form
 *  holds no second opinion about a ceiling. Nothing is computed here — in
 *  particular the page never adds the typed quantity to a cached `used`, which
 *  would be a copy of the rule living in the browser. */
function usePackCeiling({ open, doctorId, product, issuedDate, quantity, salesOfficerId }) {
  const [standing, setStanding] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) { setStanding(null); setError(""); return; }
    let cancelled = false;
    const qty = parseInt(quantity, 10);
    api
      .get("/samples/ucpmp/doctor", {
        params: {
          doctor_id: doctorId || undefined,
          product: product || undefined,
          issued_date: issuedDate || undefined,
          quantity: Number.isFinite(qty) && qty > 0 ? qty : undefined,
          sales_officer_id: salesOfficerId || undefined,
        },
      })
      .then(({ data }) => { if (!cancelled) { setStanding(data); setError(""); } })
      .catch((e) => {
        if (cancelled) return;
        // The reading is gone, so the panel says so. It must not fall back to a
        // stale standing, and above all not to silence.
        setStanding(null);
        setError(ucpmpFailure(e));
      });
    return () => { cancelled = true; };
  }, [open, doctorId, product, issuedDate, quantity, salesOfficerId]);

  return { standing, error };
}

/** The first WHOLE pack count that trips the UCPMP warning.
 *
 *  `warn_at` IS A THRESHOLD, NOT A PACK COUNT. 80% of a twelve-pack ceiling is
 *  9.6, and the clear line rendered it as though it were a count a doctor
 *  could stand at: "no doctor is at 9.6 packs of any one drug". Nobody can be
 *  at 9.6 packs. A pack is a whole unit — `quantity` is an int on the model,
 *  and every `used` figure in this module is a sum of ints — so the sentence
 *  described a state the data cannot hold.
 *
 *  THE THRESHOLD IS NOT ROUNDED; THE SENTENCE IS. `ucpmp.warn_threshold` keeps
 *  9.6 on purpose, and its docstring rules why: moving the trigger to a whole
 *  number "would state a threshold nobody configured", and whether the warning
 *  should fire on whole packs is a Settings question. That ruling is about the
 *  DATA and still stands — this changes no threshold. It is about the WORDS:
 *  the backend warns when `used >= warn_at`, and because `used` is an integer
 *  that is exactly `used >= ceil(warn_at)`. Ten is not 9.6 rounded off; it is
 *  the smallest pack count that trips a 9.6 threshold, which is the fact a rep
 *  can act on and the one thing the old sentence could not say.
 *
 *  DERIVED, NEVER HARDCODED. Neither 9.6 nor 12 appears here. A Director
 *  moving the ceiling or the fraction in Settings moves this number with them,
 *  because the only input is the threshold the server computed.
 *
 *  Returns null for a threshold that did not arrive — `{undefined} packs` used
 *  to render as a hole in the middle of a sentence. The caller then states the
 *  fact without inventing a number (Pattern A).
 */
export function warnAtPacks(warnAt) {
  if (warnAt === null || warnAt === undefined || warnAt === "") return null;
  const n = Number(warnAt);
  if (!Number.isFinite(n)) return null;
  return Math.ceil(n);
}

/** THE UCPMP PANEL (RC-25, P1). The pack ceiling is computed and shown; the
 *  money ceilings are shown in whichever state they are in — see ValueCeiling.
 *  Advisory throughout — nothing here blocks anything. */
export function UcpmpPanel({ data, error }) {
  if (error) {
    return (
      <div
        className="rounded-md border border-orange-200 bg-orange-50 text-orange-800 text-sm px-3 py-2"
        data-testid="ucpmp-error"
      >
        UCPMP ceilings could not be loaded: {error}
      </div>
    );
  }
  if (!data) return null;
  const pack = data.pack_ceiling || {};
  const breaching = pack.breaching || [];
  const warning = pack.warning || [];
  const values = data.value_ceilings || {};
  const warnPacks = warnAtPacks(pack.warn_at);

  return (
    <div className="bg-white border border-callisto rounded-xl p-4 space-y-3" data-testid="ucpmp-panel">
      <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-callisto-gold">
        UCPMP ceilings — {data.year}
      </p>

      <div data-testid="ucpmp-pack-ceiling">
        <p className="text-sm font-semibold text-callisto-navy">
          {pack.ceiling} packs per drug, per doctor, per year
        </p>
        {breaching.length === 0 && warning.length === 0 ? (
          <p className="text-[11px] text-callisto-muted" data-testid="ucpmp-pack-clear">
            {/* A COUNT, not a bare "all clear". "Nothing to report" and "nothing
                was checked" are different states and must not print the same. */}
            {pack.entries_counted} ledger entr{pack.entries_counted === 1 ? "y" : "ies"} checked
            {/* "has reached N", not "is at N": the warning fires on `used >=
                warn_at`, so the clear line is denying a THRESHOLD was crossed,
                not denying one exact count. `is at` was wrong about the
                comparison as well as about the number. */}
            {" · "}
            {warnPacks === null
              ? "no doctor is close to the limit on any one drug"
              : `no doctor has reached ${warnPacks} packs of any one drug`}
          </p>
        ) : (
          <ul className="mt-1 space-y-1">
            {breaching.map((p) => (
              <li key={`b-${p.doctor_id}-${p.product}`} className="text-[11px] text-red-700 font-semibold"
                  data-testid={`ucpmp-breach-${p.product}`}>
                Over the limit — {p.doctor_name}: {p.used} of {p.ceiling} packs of {p.product}.
                Recorded, and the Admin Manager and Director have been notified.
              </li>
            ))}
            {warning.map((p) => (
              <li key={`w-${p.doctor_id}-${p.product}`} className="text-[11px] text-orange-700"
                  data-testid={`ucpmp-warning-${p.product}`}>
                Approaching the limit — {p.doctor_name}: {p.used} of {p.ceiling} packs of {p.product}.
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="border-t border-callisto pt-3 space-y-2" data-testid="ucpmp-value-ceilings">
        {/* NOT ZERO, AND NOT SILENT — in either state. A 0 renders as
            comfortably compliant, which is a claim about a number this system
            does not hold; and a blanket "not computable" hides the ceiling the
            Director's own sales basis now produces. */}
        {[
          ["promotional_spend", `${values.promotional_spend?.limit_pct}% of domestic sales`],
          ["gift_value", `₹${values.gift_value?.limit_rupees} gift cap`],
        ].map(([key, label]) => (
          <ValueCeiling key={key} testId={`ucpmp-${key}`} label={label} block={values[key]} />
        ))}
      </div>
    </div>
  );
}

export default function SamplesPage() {
  const { user } = useAuth();
  const role = user?.role;
  if (role === "sales_officer") return <MySamplesPage />;
  return <TeamSamplesPage />;
}

function MySamplesPage() {
  const [rows, setRows] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [products, setProducts] = useState([]);
  const [balance, setBalance] = useState(null);
  const [ucpmp, setUcpmp] = useState(null);
  const [ucpmpError, setUcpmpError] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [openForm, setOpenForm] = useState(false);

  async function load() {
    setLoading(true);
    setError("");
    setUcpmpError("");
    try {
      const [list, bal, docs, prods, ceilings] = await Promise.all([
        api.get("/samples"),
        api.get("/samples/balance"),
        api.get("/doctors"),
        api.get("/settings/products"),
        // ADVISORY, so its failure must not take the page down with it — but it
        // must not vanish either. A ceilings panel that silently disappears when
        // the call fails is a compliance surface that stops existing exactly
        // when something is wrong.
        api.get("/samples/ucpmp").catch((e) => {
          setUcpmpError(ucpmpFailure(e));
          return { data: null };
        }),
      ]);
      setRows(list.data);
      setBalance(bal.data);
      setDoctors(docs.data.filter((d) => d.is_active));
      setProducts(prods.data.items || []);
      setUcpmp(ceilings.data);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { load(); }, []);

  return (
    <div className="space-y-6" data-testid="my-samples-page">
      <StickyHeading
        icon={Sparkles}
        label="Sample Log"
        title="My samples"
        subtitle={
          balance
            ? `${balance.total_units} unit${balance.total_units === 1 ? "" : "s"} issued this month across ${balance.by_product?.length || 0} product${(balance.by_product?.length || 0) === 1 ? "" : "s"}`
            : "Loading…"
        }
        testId="my-samples-heading"
        actions={
          <Button
            onClick={() => setOpenForm(true)}
            className="h-11 bg-callisto-navy hover:bg-callisto-navy/90 text-white font-semibold"
            data-testid="samples-issue-button"
          >
            <Plus size={14} className="mr-1.5" />
            Log sample
          </Button>
        }
      />

      {error && <div className="rounded-md border border-red-200 bg-red-50 text-red-700 text-sm px-3 py-2">{error}</div>}

      {/* Balance by product */}
      {!loading && balance?.by_product?.length > 0 && (
        <div className="bg-white border border-callisto rounded-xl p-4">
          <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-callisto-gold mb-3">
            This month — by product
          </p>
          <div className="flex flex-wrap gap-2" data-testid="samples-balance-chips">
            {balance.by_product.map((b) => (
              <div key={b.product} className="bg-callisto-app border border-callisto rounded-lg px-3 py-2 min-w-[120px]" data-testid={`samples-balance-${b.product}`}>
                <p className="text-[11px] text-callisto-muted">{b.product}</p>
                <p className="text-xl font-bold text-callisto-navy">{b.quantity} <span className="text-[11px] font-semibold text-callisto-muted">units</span></p>
                <p className="text-[10px] text-callisto-muted">{b.count} issuance{b.count === 1 ? "" : "s"}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {!loading && <UcpmpPanel data={ucpmp} error={ucpmpError} />}

      {loading ? (
        <Skeleton className="h-48 w-full rounded-xl" />
      ) : rows.length === 0 ? (
        <div className="bg-white border border-callisto rounded-xl p-10 flex flex-col items-center text-center" data-testid="samples-empty">
          <div className="h-14 w-14 rounded-xl bg-callisto-navy/5 flex items-center justify-center mb-3">
            <PackageOpen className="h-6 w-6 text-callisto-navy" />
          </div>
          <p className="text-sm font-semibold text-callisto-navy">No samples logged yet</p>
          <p className="mt-1 text-xs text-callisto-muted">Click "Log sample" to record samples handed out to a doctor.</p>
        </div>
      ) : (
        <SamplesTable rows={rows} />
      )}

      <SampleIssueDialog
        open={openForm}
        onClose={() => setOpenForm(false)}
        doctors={doctors}
        products={products}
        onSaved={() => { setOpenForm(false); load(); }}
      />
    </div>
  );
}

function SampleIssueDialog({ open, onClose, doctors, products, onSaved }) {
  const [doctorId, setDoctorId] = useState("");
  const [product, setProduct] = useState("");
  const [quantity, setQuantity] = useState("1");
  const [issuedDate, setIssuedDate] = useState(todayIST());
  const [notes, setNotes] = useState("");
  const [batchNo, setBatchNo] = useState("");
  const [expiry, setExpiry] = useState("");
  const [submitting, setSubmitting] = useState(false);
  // BEFORE ISSUING, not after: the doctor's standing against this drug's
  // ceiling, refetched as the form changes.
  const { standing, error: standingError } = usePackCeiling({
    open, doctorId, product, issuedDate, quantity,
  });

  useEffect(() => {
    if (open) {
      setDoctorId("");
      setProduct("");
      setQuantity("1");
      setIssuedDate(todayIST());
      setNotes("");
      setBatchNo("");
      setExpiry("");
    }
  }, [open]);

  async function save() {
    const qty = parseInt(quantity || "0", 10);
    if (!doctorId) return toast.error("Pick a doctor");
    if (!product) return toast.error("Pick a product");
    if (!qty || qty < 1) return toast.error("Quantity must be at least 1");
    setSubmitting(true);
    try {
      await api.post("/samples", {
        doctor_id: doctorId,
        product,
        quantity: qty,
        issued_date: issuedDate,
        notes: notes.trim() || null,
        // Sent as null rather than "" when blank: the backend records the gap
        // BY NAME on the ledger row, and an empty string would read as captured.
        batch_no: batchNo.trim() || null,
        expiry: expiry.trim() || null,
      });
      toast.success("Sample logged");
      onSaved();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="samples-issue-dialog">
        <DialogHeader>
          <DialogTitle>Log a sample issuance</DialogTitle>
          <DialogDescription>Pick the doctor, product, and quantity you handed out.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label className="text-callisto-navy">Doctor</Label>
            <Select value={doctorId} onValueChange={setDoctorId}>
              <SelectTrigger className="h-11 mt-1.5 focus:ring-callisto-gold" data-testid="samples-doctor-select">
                <SelectValue placeholder="Pick a doctor" />
              </SelectTrigger>
              <SelectContent>
                {doctors.length === 0 ? (
                  <SelectItem value="_none" disabled>No doctors assigned</SelectItem>
                ) : doctors.map((d) => (
                  <SelectItem key={d.id} value={d.id}>{joinParts([d.name, d.specialization])}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-callisto-navy">Product</Label>
              <Select value={product} onValueChange={setProduct}>
                <SelectTrigger className="h-11 mt-1.5 focus:ring-callisto-gold" data-testid="samples-product-select">
                  <SelectValue placeholder="Pick" />
                </SelectTrigger>
                <SelectContent>
                  {products.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-callisto-navy">Quantity</Label>
              <Input
                type="number"
                min={1}
                max={999}
                value={quantity}
                onChange={(e) => setQuantity(e.target.value)}
                className="h-11 mt-1.5 focus-visible:ring-callisto-gold focus-visible:ring-offset-0"
                data-testid="samples-qty-input"
              />
            </div>
          </div>
          <div>
            <Label className="text-callisto-navy">Issued date</Label>
            <Input
              type="date"
              value={issuedDate}
              max={todayIST()}
              onChange={(e) => setIssuedDate(e.target.value)}
              className="h-11 mt-1.5 focus-visible:ring-callisto-gold focus-visible:ring-offset-0"
              data-testid="samples-date-input"
            />
          </div>
          <PackCeilingBeforeIssuing standing={standing} error={standingError} />
          {/* THE COMPLIANCE FIELDS (F334/F406). Advisory, not mandatory —
              blocking a field issuance on a batch number nobody has been asked
              for yet is the hard stop P1 forbids. Left blank, the ledger row
              says so rather than storing an empty string. */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-callisto-navy">Batch no.</Label>
              <Input
                value={batchNo}
                onChange={(e) => setBatchNo(e.target.value)}
                placeholder="On the pack"
                className="h-11 mt-1.5 focus-visible:ring-callisto-gold focus-visible:ring-offset-0"
                data-testid="samples-batch-input"
              />
            </div>
            <div>
              <Label className="text-callisto-navy">Expiry</Label>
              <Input
                value={expiry}
                onChange={(e) => setExpiry(e.target.value)}
                placeholder="YYYY-MM"
                className="h-11 mt-1.5 focus-visible:ring-callisto-gold focus-visible:ring-offset-0"
                data-testid="samples-expiry-input"
              />
            </div>
          </div>
          <div>
            <Label className="text-callisto-navy">Notes (optional)</Label>
            <Input
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="e.g., 1-month trial pack"
              className="h-11 mt-1.5 focus-visible:ring-callisto-gold focus-visible:ring-offset-0"
              data-testid="samples-notes-input"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={submitting}>Cancel</Button>
          <Button
            onClick={save}
            disabled={submitting}
            className="bg-callisto-navy hover:bg-callisto-navy/90 text-white"
            data-testid="samples-save"
          >
            {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Log sample"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Admin/Director dialog: log a sample on behalf of an SO, or edit an entry.
 *  The doctor list is always scoped to the owning SO — the backend enforces the
 *  same rule, so an out-of-team doctor can never be saved. */
function SampleAdminDialog({ open, target, sos, products, onClose, onSaved }) {
  const isEdit = !!target;
  const [soId, setSoId] = useState("");
  const [doctors, setDoctors] = useState([]);
  const [doctorId, setDoctorId] = useState("");
  const [product, setProduct] = useState("");
  const [quantity, setQuantity] = useState("1");
  const [issuedDate, setIssuedDate] = useState(todayIST());
  const [notes, setNotes] = useState("");
  const [batchNo, setBatchNo] = useState("");
  const [expiry, setExpiry] = useState("");
  const [submitting, setSubmitting] = useState(false);
  // THE SAME COMPONENT AS THE REP'S FORM. On an EDIT the quantity is withheld:
  // the row is already in the ledger, so projecting it again would count this
  // issuance twice and show the doctor past a ceiling they are not past.
  const { standing, error: standingError } = usePackCeiling({
    open, doctorId, product, issuedDate, salesOfficerId: soId,
    quantity: isEdit ? null : quantity,
  });

  useEffect(() => {
    if (!open) return;
    setSoId(target?.sales_officer_id || "");
    setDoctorId(target?.doctor_id || "");
    setProduct(target?.product || "");
    setQuantity(String(target?.quantity ?? "1"));
    setIssuedDate(target?.issued_date || todayIST());
    setNotes(target?.notes || "");
    // PRELOADED from the row being edited, never blanked. F312 is the same
    // shape one master over: an edit form that does not preload a field
    // silently clears it on save.
    setBatchNo(target?.batch_no || "");
    setExpiry(target?.expiry || "");
  }, [open, target]);

  // Doctors assigned to the selected SO.
  useEffect(() => {
    if (!open || !soId) { setDoctors([]); return; }
    let cancelled = false;
    api.get("/doctors", { params: { assigned_to: soId } })
      .then(({ data }) => !cancelled && setDoctors(data))
      .catch(() => !cancelled && setDoctors([]));
    return () => { cancelled = true; };
  }, [open, soId]);

  function onSoChange(v) {
    setSoId(v);
    setDoctorId(""); // doctor must belong to the newly chosen SO
  }

  async function save() {
    const qty = parseInt(quantity || "0", 10);
    if (!soId) return toast.error("Pick a recipient");
    if (!doctorId) return toast.error("Pick a doctor");
    if (!product) return toast.error("Pick a product");
    if (!qty || qty < 1) return toast.error("Quantity must be at least 1");
    setSubmitting(true);
    try {
      const body = {
        doctor_id: doctorId,
        product,
        quantity: qty,
        issued_date: issuedDate,
        notes: notes.trim() || null,
        batch_no: batchNo.trim() || null,
        expiry: expiry.trim() || null,
      };
      if (isEdit) {
        await api.put(`/samples/${target.id}`, body);
        toast.success("Sample entry updated");
      } else {
        await api.post("/samples", { ...body, sales_officer_id: soId });
        toast.success("Sample logged");
      }
      onSaved();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="samples-admin-dialog">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Edit sample entry" : "Log a sample issuance"}</DialogTitle>
          <DialogDescription>
            {isEdit
              ? "Update the entry. The recipient can't be changed — delete and re-log to reassign."
              : "Logged on behalf of the selected officer or manager. Only doctors assigned to them can be picked."}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label className="text-callisto-navy">Recipient</Label>
            <Select value={soId} onValueChange={onSoChange} disabled={isEdit}>
              <SelectTrigger className="h-11 mt-1.5 focus:ring-callisto-gold" data-testid="samples-admin-so-select">
                <SelectValue placeholder="Pick an officer or manager" />
              </SelectTrigger>
              <SelectContent>
                {sos.map((u) => <SelectItem key={u.id} value={u.id}>{u.full_name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-callisto-navy">Doctor</Label>
            <Select value={doctorId} onValueChange={setDoctorId} disabled={!soId}>
              <SelectTrigger className="h-11 mt-1.5 focus:ring-callisto-gold" data-testid="samples-admin-doctor-select">
                <SelectValue placeholder={soId ? "Pick a doctor" : "Pick a recipient first"} />
              </SelectTrigger>
              <SelectContent>
                {doctors.length === 0 ? (
                  <SelectItem value="_none" disabled>No doctors assigned to them</SelectItem>
                ) : doctors.map((d) => (
                  <SelectItem key={d.id} value={d.id}>{joinParts([d.name, d.specialization])}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-callisto-navy">Product</Label>
              <Select value={product} onValueChange={setProduct}>
                <SelectTrigger className="h-11 mt-1.5 focus:ring-callisto-gold" data-testid="samples-admin-product-select">
                  <SelectValue placeholder="Pick" />
                </SelectTrigger>
                <SelectContent>
                  {products.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-callisto-navy">Quantity</Label>
              <Input
                type="number" min={1} max={999}
                value={quantity}
                onChange={(e) => setQuantity(e.target.value)}
                className="h-11 mt-1.5 focus-visible:ring-callisto-gold focus-visible:ring-offset-0"
                data-testid="samples-admin-qty-input"
              />
            </div>
          </div>
          <div>
            <Label className="text-callisto-navy">Issued date</Label>
            <Input
              type="date" value={issuedDate} max={todayIST()}
              onChange={(e) => setIssuedDate(e.target.value)}
              className="h-11 mt-1.5 focus-visible:ring-callisto-gold focus-visible:ring-offset-0"
              data-testid="samples-admin-date-input"
            />
          </div>
          <PackCeilingBeforeIssuing standing={standing} error={standingError} />
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-callisto-navy">Batch no.</Label>
              <Input
                value={batchNo}
                onChange={(e) => setBatchNo(e.target.value)}
                placeholder="On the pack"
                className="h-11 mt-1.5 focus-visible:ring-callisto-gold focus-visible:ring-offset-0"
                data-testid="samples-admin-batch-input"
              />
            </div>
            <div>
              <Label className="text-callisto-navy">Expiry</Label>
              <Input
                value={expiry}
                onChange={(e) => setExpiry(e.target.value)}
                placeholder="YYYY-MM"
                className="h-11 mt-1.5 focus-visible:ring-callisto-gold focus-visible:ring-offset-0"
                data-testid="samples-admin-expiry-input"
              />
            </div>
          </div>
          <div>
            <Label className="text-callisto-navy">Notes (optional)</Label>
            <Input
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="e.g., 1-month trial pack"
              className="h-11 mt-1.5 focus-visible:ring-callisto-gold focus-visible:ring-offset-0"
              data-testid="samples-admin-notes-input"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={submitting}>Cancel</Button>
          <Button
            onClick={save}
            disabled={submitting}
            className="bg-callisto-navy hover:bg-callisto-navy/90 text-white"
            data-testid="samples-admin-save"
          >
            {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : (isEdit ? "Save changes" : "Log sample")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SamplesTable({ rows, showSo = false, canManage = false, onEdit, onDelete, onRestore }) {
  return (
    <div className="bg-white border border-callisto rounded-xl overflow-hidden" data-testid="samples-table">
      <ul className="divide-y divide-callisto">
        {rows.map((s) => {
          const deleted = s.is_active === false;
          return (
            <li
              key={s.id}
              className={`px-4 py-3 grid grid-cols-1 sm:grid-cols-[110px_1fr_auto] gap-2 items-center ${deleted ? "opacity-60" : ""}`}
              data-testid={`samples-row-${s.id}`}
            >
              <p className="text-[12px] text-callisto-muted font-semibold">{formatISTDayLabel(s.issued_date)}</p>
              <div className="min-w-0">
                <p className="text-sm font-semibold text-callisto-navy truncate">
                  {s.product}
                  <span className="text-[11px] font-semibold text-callisto-gold ml-2">× {s.quantity}</span>
                  {deleted && <span className="text-[10px] font-bold uppercase text-orange-600 ml-2">Deleted</span>}
                </p>
                <p className="text-[11px] text-callisto-muted truncate">
                  {showSo && s.sales_officer_name && <span className="font-semibold text-callisto-navy">{s.sales_officer_name} · </span>}
                  {s.doctor_name}{s.notes && ` · ${s.notes}`}
                </p>
                {/* THE COMPLIANCE LINE (F334/F406). Batch, expiry, value and
                    the address a sample went to — the four fields a compliance
                    record needs and the Sample Log never captured. Each one
                    renders its own absence rather than an empty space. */}
                <p className="text-[11px] flex flex-wrap gap-x-3" data-testid={`samples-compliance-${s.id}`}>
                  <ComplianceValue label="Batch" value={s.batch_no} />
                  <ComplianceValue label="Expiry" value={s.expiry} />
                  <SampleValue row={s} />
                  <ComplianceValue
                    label="To"
                    value={s.doctor_address}
                    missingText="Address not captured"
                  />
                  {s.source === "dcr" && (
                    <span className="text-callisto-muted">From DCR</span>
                  )}
                </p>
              </div>
              {canManage && (
                <div className="flex items-center gap-2 justify-end">
                  {deleted ? (
                    <Button
                      variant="outline" size="sm"
                      onClick={() => onRestore(s)}
                      className="h-8 border-callisto text-callisto-navy text-[11px]"
                      data-testid={`samples-restore-${s.id}`}
                    >
                      <RotateCcw size={11} className="mr-1" /> Restore
                    </Button>
                  ) : (
                    <>
                      <Button
                        variant="outline" size="sm"
                        onClick={() => onEdit(s)}
                        className="h-8 border-callisto text-callisto-navy text-[11px]"
                        data-testid={`samples-edit-${s.id}`}
                      >
                        <Pencil size={11} className="mr-1" /> Edit
                      </Button>
                      <Button
                        variant="outline" size="sm"
                        onClick={() => onDelete(s)}
                        className="h-8 border-red-200 text-red-700 hover:bg-red-50 text-[11px]"
                        data-testid={`samples-delete-${s.id}`}
                      >
                        <Trash2 size={11} className="mr-1" /> Delete
                      </Button>
                    </>
                  )}
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function TeamSamplesPage() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [balance, setBalance] = useState(null);
  const [ucpmp, setUcpmp] = useState(null);
  const [ucpmpError, setUcpmpError] = useState("");
  const [users, setUsers] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [soFilter, setSoFilter] = useState(ALL);
  const [productFilter, setProductFilter] = useState(ALL);
  const [showDeleted, setShowDeleted] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  // Manage rights mirror the backend gate on samples PUT/DELETE. Admin Manager
  // is included but stays team-scoped — the backend rejects any row outside it.
  const canManage = ["admin", "admin_manager", "director"].includes(user?.role);
  // A Sales Manager carries samples on their own field calls, so they log their
  // own issuances (no recipient picker — always themselves).
  const canLogOwn = user?.role === "assistant_manager";
  const [ownDoctors, setOwnDoctors] = useState([]);
  const [ownFormOpen, setOwnFormOpen] = useState(false);
  const reload = () => setReloadKey((k) => k + 1);

  async function confirmDelete() {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await api.delete(`/samples/${deleteTarget.id}`);
      toast.success("Sample entry deleted");
      setDeleteTarget(null);
      reload();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setDeleting(false);
    }
  }

  async function restoreSample(s) {
    try {
      await api.put(`/samples/${s.id}`, { is_active: true });
      toast.success("Sample entry restored");
      reload();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  }

  useEffect(() => {
    (async () => {
      try {
        const [u, p] = await Promise.all([
          api.get("/users"),
          api.get("/settings/products"),
        ]);
        // Recipients are field staff: Sales Officers and Sales Managers. Admin
        // Manager is the issuer/custodian, never a recipient.
        const filtered = u.data.filter(
          (x) => ["sales_officer", "assistant_manager"].includes(x.role) && x.is_active
        );
        // Sales Manager sees their own team plus themselves. Admin Manager
        // maintains stock company-wide, so they see everyone (like admin/director).
        const teamScoped = user?.role === "assistant_manager";
        setUsers(
          teamScoped
            ? filtered.filter((u) => u.manager_id === user.id || u.id === user.id)
            : filtered
        );
        setProducts(p.data.items || []);
      } catch (_) {}
    })();
  }, [user]);

  // Doctors this manager owns (directly or jointly) — the recipient must own the
  // doctor, same rule as DCR logging.
  useEffect(() => {
    if (!canLogOwn || !user?.id) return;
    api.get("/doctors", { params: { assigned_to: user.id } })
      .then(({ data }) => setOwnDoctors(data.filter((d) => d.is_active !== false)))
      .catch(() => setOwnDoctors([]));
  }, [canLogOwn, user, reloadKey]);

  useEffect(() => {
    setLoading(true);
    setError("");
    const params = {};
    if (soFilter !== ALL) params.so_id = soFilter;
    if (productFilter !== ALL) params.product = productFilter;
    if (showDeleted) params.include_deleted = true;
    Promise.all([
      api.get("/samples", { params }),
      api.get("/samples/balance"),
      api.get("/samples/ucpmp").catch((e) => {
        setUcpmpError(ucpmpFailure(e));
        return { data: null };
      }),
    ])
      .then(([r, b, c]) => {
        setRows(r.data);
        setBalance(b.data);
        setUcpmp(c.data);
      })
      .catch((e) => setError(formatApiErrorDetail(e.response?.data?.detail) || e.message))
      .finally(() => setLoading(false));
  }, [soFilter, productFilter, showDeleted, reloadKey]);

  const top = useMemo(
    () => (balance?.by_so || []).slice(0, 6),
    [balance]
  );

  // "across N sales officers" over a set that includes Sales Managers (RC-9).
  // Recipients are FIELD staff — Sales Officers AND Sales Managers, as this
  // page's own recipient filter says twenty lines up — so the noun has to come
  // from who is actually in the list. `by_so` carries no role, so the roles are
  // joined from the roster this page already loads; anyone it cannot match
  // counts as unknown, and `labelFor` then says "people" rather than guessing.
  const recipientNoun = useMemo(() => {
    const byId = new Map((users || []).map((u) => [u.id, u]));
    const list = balance?.by_so || [];
    const rows = list.map((s) => byId.get(s.id) || { role: null });
    return labelFor(roleBreakdown(rows), { plural: list.length !== 1 });
  }, [users, balance]);

  return (
    <div className="space-y-6" data-testid="team-samples-page">
      <StickyHeading
        icon={Sparkles}
        label="Sample Log"
        title={`Sample issuance — ${balance?.scope === "team" ? "your team" : "company"}`}
        subtitle={
          balance
            ? `${plural(balance.total_units, "unit")} this month across ${balance.by_so?.length || 0} ${recipientNoun}`
            : "Loading…"
        }
        testId="team-samples-heading"
        actions={
          <>
            {canLogOwn && (
              <Button
                onClick={() => setOwnFormOpen(true)}
                className="h-11 bg-callisto-navy hover:bg-callisto-navy/90 text-white font-semibold shrink-0"
                data-testid="samples-own-add"
              >
                <Plus size={14} className="mr-1.5" />
                Log sample
              </Button>
            )}
            {canManage && (
              <Button
                onClick={() => { setEditTarget(null); setDialogOpen(true); }}
                className="h-11 bg-callisto-navy hover:bg-callisto-navy/90 text-white font-semibold shrink-0"
                data-testid="samples-admin-add"
              >
                <Plus size={14} className="mr-1.5" />
                Log sample
              </Button>
            )}
          </>
        }
      />

      <div className="bg-white border border-callisto rounded-xl p-3 md:p-4 grid grid-cols-1 sm:grid-cols-2 gap-2">
        <Select value={soFilter} onValueChange={setSoFilter}>
          <SelectTrigger className="h-11 focus:ring-callisto-gold" data-testid="samples-filter-so">
            <SelectValue placeholder="Officer / manager" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>All officers &amp; managers</SelectItem>
            {users.map((u) => <SelectItem key={u.id} value={u.id}>{u.full_name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={productFilter} onValueChange={setProductFilter}>
          <SelectTrigger className="h-11 focus:ring-callisto-gold" data-testid="samples-filter-product">
            <SelectValue placeholder="Product" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>All products</SelectItem>
            {products.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
          </SelectContent>
        </Select>
        {canManage && (
          <label className="sm:col-span-2 inline-flex items-center gap-2 text-xs text-callisto-body cursor-pointer">
            <input
              type="checkbox"
              checked={showDeleted}
              onChange={(e) => setShowDeleted(e.target.checked)}
              className="h-4 w-4 rounded border-callisto accent-callisto-navy"
              data-testid="samples-show-deleted"
            />
            Show deleted entries
          </label>
        )}
      </div>

      {!loading && top.length > 0 && (
        <div className="bg-white border border-callisto rounded-xl p-4" data-testid="samples-top-grid">
          <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-callisto-gold mb-3">
            {/* Not "Top SOs": the same list, the same reason as the subtitle
                above — a Sales Manager carries samples too. */}
            Top recipients by units issued this month
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-2">
            {top.map((so) => (
              <div key={so.id} className="bg-callisto-app rounded-lg px-3 py-2.5 flex items-center justify-between gap-2" data-testid={`samples-top-${so.id}`}>
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-callisto-navy truncate">{so.name}</p>
                  <p className="text-[10px] text-callisto-muted">
                    {so.products.slice(0, 2).map((p) => p.product).join(", ")}
                    {so.products.length > 2 && ` +${so.products.length - 2}`}
                  </p>
                </div>
                <span className="text-base font-bold text-callisto-navy">{so.total} <span className="text-[10px] uppercase text-callisto-muted">units</span></span>
              </div>
            ))}
          </div>
        </div>
      )}

      {error && <div className="rounded-md border border-red-200 bg-red-50 text-red-700 text-sm px-3 py-2">{error}</div>}

      {!loading && <UcpmpPanel data={ucpmp} error={ucpmpError} />}

      {loading ? (
        <Skeleton className="h-48 w-full rounded-xl" />
      ) : rows.length === 0 ? (
        <div className="bg-white border border-callisto rounded-xl p-10 flex flex-col items-center text-center">
          <div className="h-14 w-14 rounded-xl bg-callisto-navy/5 flex items-center justify-center mb-3">
            <Inbox className="h-6 w-6 text-callisto-navy" />
          </div>
          <p className="text-sm font-semibold text-callisto-navy">No sample records match</p>
        </div>
      ) : (
        <SamplesTable
          rows={rows}
          showSo
          canManage={canManage}
          onEdit={(s) => { setEditTarget(s); setDialogOpen(true); }}
          onDelete={(s) => setDeleteTarget(s)}
          onRestore={restoreSample}
        />
      )}

      {canLogOwn && (
        <SampleIssueDialog
          open={ownFormOpen}
          onClose={() => setOwnFormOpen(false)}
          doctors={ownDoctors}
          products={products}
          onSaved={() => { setOwnFormOpen(false); reload(); }}
        />
      )}

      {canManage && (
        <SampleAdminDialog
          open={dialogOpen}
          target={editTarget}
          sos={users}
          products={products}
          onClose={() => { setDialogOpen(false); setEditTarget(null); }}
          onSaved={() => { setDialogOpen(false); setEditTarget(null); reload(); }}
        />
      )}

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this sample entry?</AlertDialogTitle>
            <AlertDialogDescription>
              <span className="font-semibold text-callisto-navy">
                {deleteTarget?.product} × {deleteTarget?.quantity}
              </span>{" "}
              for {deleteTarget?.doctor_name} will be hidden from the log and excluded from
              monthly totals. Sample issuance is a compliance record, so the entry is retained —
              tick “Show deleted entries” to find and restore it.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); confirmDelete(); }}
              disabled={deleting}
              className="bg-red-600 hover:bg-red-700"
              data-testid="samples-delete-confirm"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
